import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { ApiError, apiUrl } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { today } from '@/features/accounting/api'
import { useRegime } from '@/features/company/regime'
import {
  createCategory,
  createProduct,
  DISCOUNT_CHANNEL_LABELS,
  DISCOUNT_CHANNELS,
  discountIsLive,
  flattenCategories,
  listCategories,
  listLocations,
  listTaxRules,
  marginPrice,
  MARGIN_BASES,
  MARGIN_TYPES,
  PRICING_MODES,
  todayIso,
  updateProduct,
  type DiscountChannel,
  type MarginType,
  type Product,
  type ProductDiscount,
  type TaxRule,
} from '@/features/inventory/api'
import { uploadFile } from '@/features/files/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const TEXTAREA_CLASS =
  'w-full rounded-md border border-slate-300 bg-white p-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUSES = ['active', 'inactive', 'discontinued'] as const

/**
 * Which kinds of location a discount channel can meaningfully be limited to: a till sits in
 * a store; the marketplace ships from the online location, or a warehouse when there is
 * none; an order raised inside the ERP can be for any of them.
 */
const CHANNEL_LOCATION_TYPES: Record<(typeof DISCOUNT_CHANNELS)[number], string[]> = {
  pos: ['store'],
  online: ['online', 'warehouse'],
  sales_order: ['store', 'warehouse', 'online'],
}

const decimalField = (message: string) =>
  z
    .string()
    .trim()
    .min(1, message)
    .refine((value) => Number.isFinite(Number(value)), { message: 'Enter a number' })

/** A number typed into a text box, or null while it is blank or not yet a number. */
const num = (value: string | undefined): number | null => {
  if (value == null || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** Blank is allowed: which of these a product needs depends on its pricing mode. */
const optionalNumber = z
  .string()
  .trim()
  .refine((value) => value === '' || Number.isFinite(Number(value)), { message: 'Enter a number' })
  .refine((value) => value === '' || Number(value) >= 0, { message: 'Cannot be negative' })

/** One offer as the form holds it: blank strings where the API takes null. */
const discountSchema = z.object({
  name: z.string().trim().max(80, 'That name is too long'),
  pct: optionalNumber,
  starts_on: z.string(),
  ends_on: z.string(),
  channels: z.array(z.enum(DISCOUNT_CHANNELS)),
  location_ids: z.array(z.string()),
})
type DiscountValues = z.infer<typeof discountSchema>

const blankDiscount = (): DiscountValues => ({
  name: '',
  pct: '',
  starts_on: '',
  ends_on: '',
  channels: [...DISCOUNT_CHANNELS],
  location_ids: [],
})

function discountValues(entry: ProductDiscount): DiscountValues {
  return {
    name: entry.name ?? '',
    pct: entry.pct,
    starts_on: entry.starts_on ?? '',
    ends_on: entry.ends_on ?? '',
    channels: entry.channels,
    location_ids: entry.location_ids,
  }
}

function discountPayload(entry: DiscountValues): ProductDiscount {
  return {
    name: entry.name || null,
    pct: entry.pct,
    starts_on: entry.starts_on || null,
    ends_on: entry.ends_on || null,
    channels: entry.channels,
    location_ids: entry.location_ids,
  }
}

const baseSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(255, 'That name is too long'),
  description: z.string().trim().max(2000, 'That description is too long'),
  long_description: z.string().trim().max(10000, 'That description is too long'),
  category_id: z.string().min(1, 'Choose a category'),
  unit_of_measure: z.string().trim().min(1, 'Enter a unit').max(20, 'That unit is too long'),
  // Blank is legal here: in margin mode the price is worked out, and `pricingRules` below
  // is what insists on one when it is typed instead.
  price: optionalNumber,
  pricing_mode: z.enum(PRICING_MODES),
  margin_basis: z.enum(MARGIN_BASES),
  margin_type: z.enum(MARGIN_TYPES),
  margin_value: optionalNumber,
  base_price: optionalNumber,
  discount_enabled: z.boolean(),
  discounts: z.array(discountSchema).max(50, 'At most 50 discounts on one product'),
  cost: decimalField('Enter a cost').refine((value) => Number(value) >= 0, {
    message: 'Cost cannot be negative',
  }),
  tax_rate: z
    .string()
    .trim()
    .refine((value) => value === '' || Number.isFinite(Number(value)), {
      message: 'Enter a number',
    })
    .refine((value) => value === '' || (Number(value) >= 0 && Number(value) <= 100), {
      message: 'A rate is between 0 and 100',
    }),
  tax_code: z.string().trim().max(8, 'HSN/SAC codes are at most 8 characters'),
  min_stock: z.string().trim().refine((value) => value === '' || Number.isInteger(Number(value)), {
    message: 'Enter a whole number',
  }),
  max_stock: z.string().trim().refine((value) => value === '' || Number.isInteger(Number(value)), {
    message: 'Enter a whole number',
  }),
  lead_time_days: z
    .string()
    .trim()
    .refine((value) => value === '' || Number.isInteger(Number(value)), {
      message: 'Enter a whole number',
    }),
  status: z.enum(STATUSES),
  listed_on_marketplace: z.boolean(),
})

const createExtra = z.object({
  code: z.string().trim().min(1, 'Enter a product code').max(50, 'That code is too long'),
})

type FormValues = z.infer<typeof baseSchema> & Partial<z.infer<typeof createExtra>>

/** The rules that look at more than one field: what each pricing mode and discount needs. */
function pricingRules(values: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) {
  if (values.pricing_mode === 'fixed') {
    if (num(values.price) == null) {
      ctx.addIssue({ code: 'custom', path: ['price'], message: 'Enter a price' })
    }
  } else {
    if (num(values.margin_value) == null) {
      ctx.addIssue({ code: 'custom', path: ['margin_value'], message: 'Enter a markup' })
    }
    if (values.margin_basis === 'base_price' && num(values.base_price) == null) {
      ctx.addIssue({ code: 'custom', path: ['base_price'], message: 'Enter a base price' })
    }
  }
  if (values.discount_enabled && values.discounts.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['discounts'], message: 'Add at least one discount' })
  }
  // Checked with the switch off too: the list is saved either way, paused rather than lost.
  values.discounts.forEach((entry, index) => {
    const pct = num(entry.pct)
    if (pct == null || pct <= 0 || pct > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['discounts', index, 'pct'],
        message: 'Enter a percentage above 0, up to 100',
      })
    }
    if (entry.channels.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['discounts', index, 'channels'],
        message: 'Pick at least one place the discount applies',
      })
    }
    if (entry.starts_on && entry.ends_on && entry.ends_on < entry.starts_on) {
      ctx.addIssue({
        code: 'custom',
        path: ['discounts', index, 'ends_on'],
        message: 'Ends before it starts',
      })
    }
  })
}

const editSchema = baseSchema.superRefine(pricingRules)
const createSchema = baseSchema.merge(createExtra).superRefine(pricingRules)

/** One photo: the key that is stored, and a URL to show it with while the form is open. */
interface ProductImage {
  key: string
  /** The signed URL the API returned, or an object URL for something just picked. */
  src: string
}

/** A product's photos as the dialog holds them, keys paired with the URLs to preview. */
function imagesOf(product?: Product): ProductImage[] {
  const keys = product?.image_keys ?? []
  const urls = product?.images ?? []
  return keys.map((key, index) => ({ key, src: apiUrl(urls[index] ?? key) }))
}

/**
 * Create and edit on one page: the code is the one field that only exists on create,
 * because `ProductUpdate` has no `code` -- changing a SKU's code after orders have been
 * raised against it is a new product, not an edit.
 *
 * A page rather than a dialog so the price can be worked through beside the form: the
 * right-hand column shows every step from cost to what the customer pays, live, and how
 * each step is calculated -- the questions a pricing form otherwise leaves unanswered.
 */
export function ProductForm({
  product,
}: {
  /** Absent for a new product. */
  product?: Product
}) {
  const isEdit = product !== undefined
  const navigate = useNavigate()
  const backTo = product ? `/products/${product.id}` : '/products'
  // Set once the save has landed, so leaving the page is not taken for abandoning edits.
  const [savedTo, setSavedTo] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const regime = useRegime()
  const [formError, setFormError] = useState<string | null>(null)
  const [addingCategory, setAddingCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  // Photos are not a react-hook-form field: the value is an array built by uploads and
  // removals rather than typed into an input, so it is held here and folded into the save
  // payload. `imagesDirty` is what keeps the unsaved-changes guard honest about it.
  const [images, setImages] = useState<ProductImage[]>(() => imagesOf(product))
  const [imagesDirty, setImagesDirty] = useState(false)
  const [uploading, setUploading] = useState(false)
  const imageInput = useRef<HTMLInputElement>(null)

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const options = flattenCategories(categories.data ?? [])

  const locations = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: listLocations,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const everyLocation = locations.data?.data ?? []

  const taxRules = useQuery({
    queryKey: ['tax-rules', { active_on: today() }],
    queryFn: () => listTaxRules({ active_on: today() }),
    retry: false,
    staleTime: 5 * 60_000,
  })

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(isEdit ? editSchema : createSchema),
    defaultValues: {
      code: product?.code ?? '',
      name: product?.name ?? '',
      description: product?.description ?? '',
      long_description: product?.long_description ?? '',
      category_id: product?.category_id ?? '',
      unit_of_measure: product?.unit_of_measure ?? 'each',
      price: product?.price ?? '',
      cost: product?.cost ?? '',
      pricing_mode: product?.pricing_mode ?? 'fixed',
      margin_basis: product?.margin_basis ?? 'cost',
      margin_type: product?.margin_type ?? 'percent',
      margin_value: product?.margin_value ?? '',
      base_price: product?.base_price ?? '',
      discount_enabled: product?.discount_enabled ?? false,
      discounts: (product?.discounts ?? []).map(discountValues),
      tax_rate: product?.tax_rate ?? '0',
      tax_code: product?.tax_code ?? '',
      min_stock: product?.min_stock != null ? String(product.min_stock) : '0',
      max_stock: product?.max_stock != null ? String(product.max_stock) : '999999',
      lead_time_days: product?.lead_time_days != null ? String(product.lead_time_days) : '7',
      status: (product?.status as (typeof STATUSES)[number]) ?? 'active',
      listed_on_marketplace: product?.listed_on_marketplace ?? false,
    },
  })

  // Watched so the selling price can be shown as it is typed: margin mode is only useful if
  // the person can see what the margin comes to before saving.
  const [
    pricingMode,
    marginBasis,
    marginType,
    marginValue,
    basePrice,
    fixedPrice,
    costValue,
    discountEnabled,
    discounts,
    categoryId,
    taxRateValue,
  ] = watch([
    'pricing_mode',
    'margin_basis',
    'margin_type',
    'margin_value',
    'base_price',
    'price',
    'cost',
    'discount_enabled',
    'discounts',
    'category_id',
    'tax_rate',
  ])
  const discountRows = useFieldArray({ control, name: 'discounts' })
  const basisAmount = marginBasis === 'cost' ? num(costValue) : num(basePrice)
  const marginAmount = num(marginValue)
  const sellingPrice =
    pricingMode === 'fixed'
      ? num(fixedPrice)
      : basisAmount != null && marginAmount != null
        ? marginPrice(basisAmount, marginType, marginAmount)
        : null

  // The category `<select>` is uncontrolled (plain `register`), so its initial value is
  // written to the DOM once, at mount. `categories` loads async and the real `<option>`
  // elements often aren't there yet when that happens -- the browser silently drops a
  // value that names an option it hasn't rendered, and RHF never retries. Once the list
  // shows up, re-push the product's own category so the select doesn't sit on the
  // placeholder while the form state (and every other field) is fine.
  useEffect(() => {
    if (isEdit && product?.category_id && categories.data) {
      setValue('category_id', product.category_id)
    }
  }, [isEdit, product?.category_id, categories.data, setValue])

  const guard = useDirtyGuard(savedTo === null && (isDirty || imagesDirty))

  // Navigated from an effect rather than from `onSuccess`, so the guard has already been
  // told the form is saved by the time the router asks it.
  useEffect(() => {
    if (savedTo) navigate(savedTo)
  }, [savedTo, navigate])

  const cancel = () => navigate(backTo)

  const addImage = async (file: File) => {
    setUploading(true)
    try {
      const key = await uploadFile(file, 'product_image')
      // The API signs keys on read; nothing has been read yet, so the preview is the local
      // file. It is replaced by the signed URL the next time the product loads.
      setImages((current) => [...current, { key, src: URL.createObjectURL(file) }])
      setImagesDirty(true)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'The photo could not be uploaded.')
    } finally {
      setUploading(false)
      // So picking the same file again still fires a change event.
      if (imageInput.current) imageInput.current.value = ''
    }
  }

  const addCategory = useMutation({
    mutationFn: () => createCategory({ name: newCategoryName.trim() }),
    onSuccess: (category) => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      setValue('category_id', category.id, { shouldDirty: true })
      setNewCategoryName('')
      setAddingCategory(false)
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not add the category.'),
  })

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const shared = {
        name: values.name,
        description: values.description || null,
        long_description: values.long_description || null,
        category_id: values.category_id,
        unit_of_measure: values.unit_of_measure,
        cost: values.cost,
        pricing_mode: values.pricing_mode,
        // In margin mode the API works the price out from the cost or base price as it is
        // when the product is saved, and again whenever either changes later.
        ...(values.pricing_mode === 'fixed'
          ? { price: values.price }
          : {
              margin_basis: values.margin_basis,
              margin_type: values.margin_type,
              margin_value: values.margin_value,
              base_price: values.margin_basis === 'base_price' ? values.base_price : null,
            }),
        discount_enabled: values.discount_enabled,
        discounts: values.discounts.map(discountPayload),
        tax_rate: values.tax_rate || '0',
        tax_code: values.tax_code || null,
        min_stock: Number(values.min_stock || 0),
        max_stock: Number(values.max_stock || 0),
        lead_time_days: Number(values.lead_time_days || 0),
        status: values.status,
        listed_on_marketplace: values.listed_on_marketplace,
        images: images.map((image) => image.key),
      }
      if (product) return updateProduct(product.id, shared)
      return createProduct({ ...shared, code: values.code! })
    },
    onSuccess: (saved) => {
      setImagesDirty(false)
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['product', saved.id] })
      toast.success(isEdit ? `Saved ${saved.name}.` : `Created ${saved.name}.`)
      setSavedTo(`/products/${saved.id}`)
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'A product with that code already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  // The preview shows the biggest offer running today on any channel -- what the busiest
  // customer would be charged -- with the channels and stores it is limited to.
  const day = todayIso()
  const todays = discountEnabled
    ? discounts
        .filter((entry) => (num(entry.pct) ?? 0) > 0 && discountIsLive(entry, day))
        .reduce<DiscountValues | null>(
          (best, entry) => (best && num(best.pct)! >= num(entry.pct)! ? best : entry),
          null,
        )
    : null
  const locationNames = (ids: string[]) =>
    ids.length === 0
      ? null
      : everyLocation.filter((location) => ids.includes(location.id)).map((location) => location.name)

  return (
    <div className="space-y-4">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {isEdit ? product.name : 'All products'}
      </Link>

      <header>
        <h1 className="text-xl font-semibold tracking-tight">
          {isEdit ? `Edit ${product.name}` : 'New product'}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {isEdit
            ? 'Price and cost changes apply to every order raised from now on, not to ones already placed.'
            : 'Added to the active catalog as soon as you save.'}
        </p>
      </header>

      <div className="grid gap-6 desktop:grid-cols-[minmax(0,1fr)_24rem] desktop:items-start">
        <Card>
        <form id="product-form" onSubmit={onSubmit} noValidate className="space-y-4 p-5">
          {formError && <Alert tone="error">{formError}</Alert>}

          {!isEdit && (
            <Field label="Code" error={errors.code?.message}>
              {(props) => <Input {...props} {...register('code')} autoComplete="off" />}
            </Field>
          )}

          <Field label="Name" error={errors.name?.message}>
            {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
          </Field>

          <Field label="Description" error={errors.description?.message} hint="Optional. One line, shown under the name.">
            {(props) => <Input {...props} {...register('description')} autoComplete="off" />}
          </Field>

          <Field
            label="Long description"
            error={errors.long_description?.message}
            hint="Optional. Ingredients, usage, pack details — shown on the product page and the marketplace. Leave a blank line between paragraphs."
          >
            {(props) => (
              <textarea {...props} {...register('long_description')} rows={6} className={TEXTAREA_CLASS} />
            )}
          </Field>

          <Field label="Category" error={errors.category_id?.message}>
            {(props) => (
              <div className="space-y-2">
                <select {...props} {...register('category_id')} className={SELECT_CLASS}>
                  <option value="">Choose a category…</option>
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {addingCategory ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newCategoryName}
                      onChange={(event) => setNewCategoryName(event.target.value)}
                      placeholder="New category name"
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={addCategory.isPending}
                      disabled={!newCategoryName.trim()}
                      onClick={() => addCategory.mutate()}
                    >
                      Add
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAddingCategory(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAddingCategory(true)}>
                    + New category
                  </Button>
                )}
              </div>
            )}
          </Field>

          <Field label="Cost" error={errors.cost?.message} hint="Not shown to sellers.">
            {(props) => (
              <Input {...props} {...register('cost')} inputMode="decimal" className="tabular-nums" />
            )}
          </Field>

          <fieldset className="space-y-4 rounded-md border border-slate-200 p-4 dark:border-slate-700">
            <legend className="px-1 text-sm font-medium text-slate-900 dark:text-slate-100">
              Selling price
            </legend>

            <Field label="How is the price set?" error={errors.pricing_mode?.message}>
              {(props) => (
                <select {...props} {...register('pricing_mode')} className={SELECT_CLASS}>
                  <option value="fixed">Type the price myself</option>
                  <option value="margin">Add a markup to a basis</option>
                </select>
              )}
            </Field>

            {pricingMode === 'fixed' ? (
              <Field label="Price" error={errors.price?.message}>
                {(props) => (
                  <Input {...props} {...register('price')} inputMode="decimal" className="tabular-nums" />
                )}
              </Field>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Markup on" error={errors.margin_basis?.message}>
                    {(props) => (
                      <select {...props} {...register('margin_basis')} className={SELECT_CLASS}>
                        <option value="cost">Cost</option>
                        <option value="base_price">A base price</option>
                      </select>
                    )}
                  </Field>
                  <Field label="Markup type" error={errors.margin_type?.message}>
                    {(props) => (
                      <select {...props} {...register('margin_type')} className={SELECT_CLASS}>
                        <option value="percent">Percentage (%)</option>
                        <option value="amount">Fixed amount</option>
                      </select>
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {marginBasis === 'base_price' && (
                    <Field label="Base price" error={errors.base_price?.message}>
                      {(props) => (
                        <Input {...props} {...register('base_price')} inputMode="decimal" className="tabular-nums" />
                      )}
                    </Field>
                  )}
                  <Field
                    label={marginType === 'percent' ? 'Markup (%)' : 'Markup (amount)'}
                    error={errors.margin_value?.message}
                  >
                    {(props) => (
                      <Input {...props} {...register('margin_value')} inputMode="decimal" className="tabular-nums" />
                    )}
                  </Field>
                </div>
              </>
            )}

            {pricingMode === 'margin' && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {sellingPrice == null
                  ? `Enter the ${marginBasis === 'cost' ? 'cost' : 'base price'} and a markup to see the price.`
                  : `Selling price: ${sellingPrice.toFixed(2)}. It follows the ${
                      marginBasis === 'cost' ? 'cost' : 'base price'
                    } and markup -- change either later and the price moves with it.`}
              </p>
            )}
          </fieldset>

          <fieldset className="space-y-4 rounded-md border border-slate-200 p-4 dark:border-slate-700">
            <legend className="px-1 text-sm font-medium text-slate-900 dark:text-slate-100">
              Discount
            </legend>
            <label className="flex items-center gap-2 text-sm text-slate-900 dark:text-slate-100">
              <input
                type="checkbox"
                {...register('discount_enabled', {
                  onChange: (event) => {
                    // Switching on with nothing set up opens a first offer to fill in.
                    if (event.target.checked && discountRows.fields.length === 0) {
                      discountRows.append(blankDiscount())
                    }
                  },
                })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-700"
              />
              Give a discount on this product
            </label>

            {discountEnabled && (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Leave the dates blank for an offer that always runs. Offers that overlap do
                  not add up: on a day two run, the bigger one applies.
                </p>
                {errors.discounts?.message && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.discounts.message}</p>
                )}

                {discountRows.fields.map((row, index) => {
                  const entry = discounts[index] ?? blankDiscount()
                  const entryErrors = errors.discounts?.[index]
                  // Only the locations that make sense for this offer's channels. The list is
                  // shared by all of them, so it is the union: ticking POS alone leaves out
                  // warehouses and the online one.
                  const fitting = (channels: DiscountChannel[]) =>
                    everyLocation.filter((location) =>
                      channels.some((channel) => CHANNEL_LOCATION_TYPES[channel].includes(location.type)),
                    )
                  const choices = fitting(entry.channels)
                  return (
                    <div
                      key={row.id}
                      className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <DiscountStatus entry={entry} />
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => discountRows.remove(index)}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                        <Field label="Name (optional)" error={entryErrors?.name?.message}>
                          {(props) => (
                            <Input
                              {...props}
                              {...register(`discounts.${index}.name`)}
                              placeholder="e.g. Diwali offer"
                            />
                          )}
                        </Field>
                        <Field label="Discount (%)" error={entryErrors?.pct?.message}>
                          {(props) => (
                            <Input
                              {...props}
                              {...register(`discounts.${index}.pct`)}
                              inputMode="decimal"
                              className="tabular-nums"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <Field label="From" error={entryErrors?.starts_on?.message}>
                          {(props) => (
                            <Input {...props} type="date" {...register(`discounts.${index}.starts_on`)} />
                          )}
                        </Field>
                        <Field label="To (last day)" error={entryErrors?.ends_on?.message}>
                          {(props) => (
                            <Input {...props} type="date" {...register(`discounts.${index}.ends_on`)} />
                          )}
                        </Field>
                      </div>

                      <CheckGroup
                        label="Applies to"
                        error={entryErrors?.channels?.message}
                        options={DISCOUNT_CHANNELS.map((channel) => ({
                          value: channel,
                          label: DISCOUNT_CHANNEL_LABELS[channel],
                        }))}
                        selected={entry.channels}
                        onChange={(next) => {
                          const channels = next as DiscountChannel[]
                          setValue(`discounts.${index}.channels`, channels, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          // A location that has dropped out of the list must not stay
                          // selected where it can no longer be seen. If that empties a
                          // limited selection, fall back to every location still listed
                          // rather than to "everywhere", which would quietly widen the offer.
                          if (entry.location_ids.length > 0) {
                            const fits = fitting(channels)
                            const kept = entry.location_ids.filter((id) =>
                              fits.some((location) => location.id === id),
                            )
                            if (kept.length !== entry.location_ids.length) {
                              setValue(
                                `discounts.${index}.location_ids`,
                                kept.length > 0 ? kept : fits.map((location) => location.id),
                                { shouldDirty: true },
                              )
                            }
                          }
                        }}
                      />

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-slate-900 dark:text-slate-100">
                          <input
                            type="checkbox"
                            checked={entry.location_ids.length === 0}
                            onChange={(event) =>
                              setValue(
                                `discounts.${index}.location_ids`,
                                event.target.checked ? [] : choices.map((location) => location.id),
                                { shouldDirty: true },
                              )
                            }
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-700"
                          />
                          All locations
                        </label>
                        {entry.location_ids.length > 0 && (
                          <CheckGroup
                            label="Only at these locations"
                            options={choices.map((location) => ({
                              value: location.id,
                              label: location.name,
                            }))}
                            selected={entry.location_ids}
                            onChange={(next) => {
                              // An empty list means "all locations", so unticking the last
                              // one would quietly widen the offer. Going wide is the box above.
                              if (next.length > 0) {
                                setValue(`discounts.${index}.location_ids`, next, { shouldDirty: true })
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => discountRows.append(blankDiscount())}
                >
                  Add another discount
                </Button>
              </>
            )}
          </fieldset>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Unit of measure" error={errors.unit_of_measure?.message}>
              {(props) => <Input {...props} {...register('unit_of_measure')} autoComplete="off" />}
            </Field>
            <Field
              label="Tax rate (%)"
              error={errors.tax_rate?.message}
              hint={
                regime.rate_slabs
                  ? `${regime.name} allows a fixed set of rates.`
                  : 'Checked against the active tax regime on save.'
              }
            >
              {(props) =>
                regime.rate_slabs ? (
                  <select {...props} {...register('tax_rate')} className={SELECT_CLASS}>
                    {regime.rate_slabs.map((slab) => (
                      <option key={slab} value={slab}>
                        {slab}%
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input {...props} {...register('tax_rate')} inputMode="decimal" className="tabular-nums" />
                )
              }
            </Field>
          </div>

          <Field
            label={regime.product_code_label}
            error={errors.tax_code?.message}
            hint="Optional here; required on a tax invoice raised under a regime that needs it."
          >
            {(props) => <Input {...props} {...register('tax_code')} autoComplete="off" />}
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Min stock" error={errors.min_stock?.message}>
              {(props) => <Input {...props} {...register('min_stock')} inputMode="numeric" />}
            </Field>
            <Field label="Max stock" error={errors.max_stock?.message}>
              {(props) => <Input {...props} {...register('max_stock')} inputMode="numeric" />}
            </Field>
            <Field label="Lead time (days)" error={errors.lead_time_days?.message}>
              {(props) => <Input {...props} {...register('lead_time_days')} inputMode="numeric" />}
            </Field>
          </div>

          <Field label="Status" error={errors.status?.message}>
            {(props) => (
              <select {...props} {...register('status')} className={SELECT_CLASS}>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status[0].toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="Marketplace"
            error={errors.listed_on_marketplace?.message}
            hint="Whether shoppers can buy this on the public storefront. Active status alone does not list it."
          >
            {(props) => (
              <div className="flex h-10 items-center gap-2 text-sm text-slate-900 dark:text-slate-100">
                <input
                  {...props}
                  {...register('listed_on_marketplace')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-700"
                />
                <span>Listed on marketplace</span>
              </div>
            )}
          </Field>

          <Field
            label="Photos"
            hint="Shown on the storefront. The first one is the tile the shopper sees in the grid."
          >
            {() => (
              <div className="space-y-3">
                {images.length > 0 && (
                  <ul className="flex flex-wrap gap-3">
                    {images.map((image) => (
                      <li key={image.key} className="relative">
                        <img
                          src={image.src}
                          alt=""
                          className="h-20 w-20 rounded-md border border-slate-200 object-cover dark:border-slate-700"
                        />
                        <button
                          type="button"
                          aria-label="Remove photo"
                          onClick={() => {
                            setImages((current) =>
                              current.filter((held) => held.key !== image.key),
                            )
                            setImagesDirty(true)
                          }}
                          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-xs text-slate-600 shadow-sm hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  ref={imageInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  aria-label="Choose a product photo"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) void addImage(file)
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  loading={uploading}
                  onClick={() => imageInput.current?.click()}
                >
                  {images.length > 0 ? 'Add another photo' : 'Upload a photo'}
                </Button>
              </div>
            )}
          </Field>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
            <Button variant="secondary" onClick={cancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create product'}
            </Button>
          </div>
        </form>
        </Card>

        <aside className="space-y-4 desktop:sticky desktop:top-4">
          <PriceWorkings
            cost={num(costValue)}
            basisLabel={marginBasis === 'cost' ? 'Cost' : 'Base price'}
            basis={pricingMode === 'margin' ? basisAmount : null}
            markupType={marginType}
            markup={pricingMode === 'margin' ? marginAmount : null}
            price={sellingPrice}
            discountPct={todays ? num(todays.pct) : null}
            discountName={todays?.name || null}
            discountChannels={todays?.channels ?? []}
            discountLocationNames={todays ? locationNames(todays.location_ids) : null}
            tax={effectiveTax(taxRules.data, product?.id, categoryId, num(taxRateValue))}
          />
          <PricingHelp />
        </aside>
      </div>

      <UnsavedChangesDialog guard={guard} />
    </div>
  )
}

/** The GST rate a sale of this product would be charged today, and where it comes from. */
interface EffectiveTax {
  rate: number | null
  /** "Tax rule: Snacks 18%", or why the product's own field is the one in use. */
  source: string
}

/**
 * Same precedence as `catalog.service.resolve_tax_rate` -- product rule, category rule,
 * catch-all, newest start first, and the product's own field only when none applies -- so
 * the figure shown here is the one an invoice will charge, not the one typed below.
 */
function effectiveTax(
  rules: TaxRule[] | undefined,
  productId: string | undefined,
  categoryId: string,
  ownRate: number | null,
): EffectiveTax {
  const newestFirst = [...(rules ?? [])].sort((a, b) => b.valid_from.localeCompare(a.valid_from))
  const rule =
    (productId && newestFirst.find((r) => r.product_id === productId)) ||
    (categoryId && newestFirst.find((r) => r.product_id === null && r.category_id === categoryId)) ||
    newestFirst.find((r) => r.product_id === null && r.category_id === null)
  if (rule) return { rate: Number(rule.rate), source: `Tax rule: ${rule.name}` }
  return { rate: ownRate, source: "This product's own tax rate (no tax rule covers it)" }
}

const fmt = (value: number) => value.toFixed(2)
const round2 = (value: number) => Math.round(value * 100) / 100

function Row({
  label,
  value,
  note,
  strong,
  tone,
}: {
  label: string
  value: string
  note?: string
  strong?: boolean
  tone?: 'bad'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <dt className="text-slate-600 dark:text-slate-300">
        {label}
        {note && <span className="block text-xs text-slate-500 dark:text-slate-400">{note}</span>}
      </dt>
      <dd
        className={
          'shrink-0 text-right tabular-nums ' +
          (strong ? 'font-semibold ' : '') +
          (tone === 'bad' ? 'text-rose-600' : 'text-slate-900 dark:text-slate-100')
        }
      >
        {value}
      </dd>
    </div>
  )
}

/**
 * Every step from cost to what the customer pays for one unit, recomputed on each
 * keystroke. A preview: the API works the stored price out itself on save.
 */
function PriceWorkings({
  cost,
  basisLabel,
  basis,
  markupType,
  markup,
  price,
  discountPct,
  discountName,
  discountChannels,
  discountLocationNames,
  tax,
}: {
  cost: number | null
  basisLabel: string
  /** Null in fixed mode, where the price is typed rather than worked out. */
  basis: number | null
  markupType: MarginType
  markup: number | null
  price: number | null
  /** Today's biggest running offer, or null when none runs today. */
  discountPct: number | null
  discountName: string | null
  discountChannels: DiscountChannel[]
  /** Null when the discount applies at every location. */
  discountLocationNames: string[] | null
  tax: EffectiveTax
}) {
  const hasDiscount = discountPct != null && discountPct > 0 && price != null
  const charged = price == null ? null : hasDiscount ? round2((price * (100 - discountPct)) / 100) : price
  const taxAmount = charged != null && tax.rate != null ? round2((charged * tax.rate) / 100) : null
  const profit = charged != null && cost != null ? round2(charged - cost) : null

  return (
    <Card>
      <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Price calculation
        </h2>
        <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
          One unit, worked out from what is filled in on the left.
        </p>
      </div>
      {price == null ? (
        <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
          Fill in the price, or a basis and a markup, to see the working.
        </p>
      ) : (
        <dl data-testid="price-preview" className="divide-y divide-slate-100 px-5 py-2 text-sm dark:divide-slate-800">
          <div className="py-1">
            {basis != null && markup != null && (
              <>
                <Row label={basisLabel} value={fmt(basis)} />
                <Row
                  label={markupType === 'percent' ? `+ Markup ${markup}%` : '+ Markup'}
                  note={
                    markupType === 'percent'
                      ? `${fmt(basis)} × ${markup}% = ${fmt(round2(price - basis))}`
                      : undefined
                  }
                  value={fmt(round2(price - basis))}
                />
              </>
            )}
            <Row label="List price" value={fmt(price)} strong />
          </div>

          {hasDiscount && charged != null && (
            <div className="py-1">
              <Row
                label={`− ${discountName ?? 'Product discount'} ${discountPct}%`}
                note={`Today, on ${discountChannels.map((c) => DISCOUNT_CHANNEL_LABELS[c]).join(', ') || 'no channel yet'}; ${
                  discountLocationNames ? `only at ${discountLocationNames.join(', ')}` : 'all locations'
                }`}
                value={fmt(round2(price - charged))}
              />
              <Row label="Customer pays, before GST" value={fmt(charged)} strong />
            </div>
          )}

          {charged != null && (
            <div className="py-1">
              <Row
                label={tax.rate != null ? `+ GST ${tax.rate}%` : '+ GST'}
                note={tax.source}
                value={taxAmount != null ? fmt(taxAmount) : '—'}
              />
              <Row
                label="Total with GST"
                value={taxAmount != null ? fmt(round2(charged + taxAmount)) : '—'}
                strong
              />
            </div>
          )}

          {profit != null && charged != null && cost != null && (
            <div className="py-1">
              <Row label="Cost" value={fmt(cost)} />
              <Row
                label="Left after cost"
                note="Before GST, which is passed on to the government"
                value={fmt(profit)}
                strong
                tone={profit < 0 ? 'bad' : undefined}
              />
              {charged > 0 && (
                <Row
                  label="Margin"
                  note="Left after cost ÷ customer price"
                  value={`${((profit / charged) * 100).toFixed(1)}%`}
                />
              )}
              {cost > 0 && (
                <Row
                  label="Markup on cost"
                  note="Left after cost ÷ cost"
                  value={`${((profit / cost) * 100).toFixed(1)}%`}
                />
              )}
            </div>
          )}

          {charged != null && (
            <p className="py-2 text-xs text-slate-500 dark:text-slate-400">
              Commission is paid on {fmt(charged)} a unit (after discounts, before GST), at the
              rate set under Commission rules.
            </p>
          )}
        </dl>
      )}
    </Card>
  )
}

function HelpSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1">
      <h3 className="font-medium text-slate-900 dark:text-slate-100">{title}</h3>
      {children}
    </section>
  )
}

/** The rules behind the numbers above, in the order a sale applies them. */
function PricingHelp() {
  return (
    <Card>
      <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          How pricing works
        </h2>
      </div>
      <div className="space-y-4 px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
        <HelpSection title="1. List price">
          <p>Either type it, or let it follow a basis with a markup:</p>
          <ul className="list-disc space-y-0.5 pl-5">
            <li>Percentage: basis × (100 + markup) ÷ 100. Cost 60 + 50% = 90.</li>
            <li>Fixed amount: basis + markup. Cost 60 + 25 = 85.</li>
          </ul>
          <p>
            The basis is the cost, or a separate base price such as an MRP or a distributor
            list. Change the basis later and the price follows it. Amounts round to 2 decimals.
          </p>
        </HelpSection>

        <HelpSection title="2. Product discount">
          <p>
            A percentage off the list price, only on the channels ticked (counter, online
            store, sales orders), optionally only at chosen locations, and optionally only
            between two dates (both days included). Add as many as you need: a festival offer
            and a longer sale can overlap, and on a day both run the bigger one applies --
            they never add up, and neither changes the other&apos;s dates. The list price
            itself does not change: documents show it, then the discount. Untick the box and
            every offer pauses.
          </p>
        </HelpSection>

        <HelpSection title="3. Discounts at the time of sale">
          <p>
            A seller can take an amount off a line, or off the whole bill. A bill discount is
            spread over the lines by their value, before GST, so tax is charged only on what is
            actually paid. On a sales order, conceding more than the approval limit (10% unless
            changed) below the price above means someone other than the seller must approve the
            order. The product discount does not count towards that limit.
          </p>
        </HelpSection>

        <HelpSection title="4. GST">
          <p>The rate is taken from the first of these that exists:</p>
          <ol className="list-decimal space-y-0.5 pl-5">
            <li>a tax rule for this product</li>
            <li>a tax rule for its category</li>
            <li>a catch-all tax rule</li>
            <li>the tax rate typed on this product</li>
          </ol>
          <p>GST = price after discounts × rate. A tax-exempt customer pays none.</p>
        </HelpSection>

        <HelpSection title="5. Commission">
          <p>
            Paid on the amount after every discount and before GST, at the rate the commission
            rules give for the seller and the product's category.
          </p>
        </HelpSection>

        <HelpSection title="Margin or markup?">
          <p>
            Both measure what is left after cost. Markup divides it by the cost; margin divides
            it by the selling price. Cost 60, price 90: 30 left, a 50% markup, a 33.3% margin.
          </p>
        </HelpSection>
      </div>
    </Card>
  )
}

/** Where an offer stands today: running, not started yet, or over (kept as a record). */
function DiscountStatus({ entry }: { entry: DiscountValues }) {
  const day = todayIso()
  const [text, tone] = discountIsLive(entry, day)
    ? ['Running today', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300']
    : entry.starts_on && entry.starts_on > day
      ? [`Starts ${entry.starts_on}`, 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300']
      : ['Ended', 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400']
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{text}</span>
}

/** A row of checkboxes over a list of values, held by the parent rather than by react-hook-form. */
function CheckGroup({
  label,
  options,
  selected,
  onChange,
  error,
}: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  error?: string
}) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 text-sm text-slate-900 dark:text-slate-100"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, option.value]
                    : selected.filter((value) => value !== option.value),
                )
              }
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-700"
            />
            {option.label}
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  )
}
